import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { emailKeys } from "@/lib/query";

export type EmailTemplate = "test" | "welcome" | "password-reset" | "notification";

export interface OutboxRow {
  id: string;
  toAddresses: string[];
  subject: string;
  templateName: string | null;
  status:
    | "pending"
    | "queued"
    | "sending"
    | "sent"
    | "delivered"
    | "bounced"
    | "complained"
    | "failed";
  attempts: number;
  lastError: string | null;
  createdAt: string;
  sentAt: string | null;
}

export interface TemplateContent {
  subject: string;
  preheader: string;
  heading: string;
  bodyParagraphs: string[];
  ctaLabel: string | null;
}

export interface TemplateSummary {
  key: string;
  label: string;
  editable: boolean;
  content: TemplateContent;
  defaultContent: TemplateContent;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface TemplateDetail extends TemplateSummary {
  sampleData: Record<string, unknown>;
}

interface SendTestEmailInput {
  to: string;
  template: EmailTemplate;
}

interface SendTestEmailResult {
  ids: string[];
  suppressed: string[];
  blocked: number;
}

export function useSendTestEmail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SendTestEmailInput): Promise<SendTestEmailResult> => {
      const res = await fetch("/api/admin/email/send-test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Send failed" }));
        throw new Error(body.error ?? "Send failed");
      }
      return res.json();
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: emailKeys.all });
      if (result.blocked > 0) {
        toast.warning("Not sent: that address isn't on the allowed list here");
      } else if (result.suppressed.length > 0) {
        toast.warning(`Recipient is on the suppression list: ${result.suppressed.join(", ")}`);
      } else {
        toast.success("Test email queued");
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to send test email");
    },
  });
}

export function useRecentEmails() {
  return useQuery({
    queryKey: emailKeys.outbox(),
    queryFn: async (): Promise<OutboxRow[]> => {
      const res = await fetch("/api/admin/email/outbox?limit=10");
      if (!res.ok) throw new Error("Failed to load recent emails");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useEmailTemplates() {
  return useQuery({
    queryKey: emailKeys.templates(),
    queryFn: async (): Promise<TemplateSummary[]> => {
      const res = await fetch("/api/admin/email/templates");
      if (!res.ok) throw new Error("Failed to load templates");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useEmailTemplate(key: string | null) {
  return useQuery({
    queryKey: emailKeys.template(key ?? ""),
    queryFn: async (): Promise<TemplateDetail> => {
      const res = await fetch(`/api/admin/email/templates/${key}`);
      if (!res.ok) throw new Error("Failed to load template");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(key),
  });
}

export function useUpdateEmailTemplate(key: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: TemplateContent): Promise<TemplateSummary> => {
      const res = await fetch(`/api/admin/email/templates/${key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(content),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Save failed" }));
        throw new Error(body.error ?? "Save failed");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailKeys.templates() });
      queryClient.invalidateQueries({ queryKey: emailKeys.template(key) });
      toast.success("Template saved");
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Failed to save template");
    },
  });
}

export interface PreviewResult {
  subject: string;
  html: string;
  text: string;
}

export function usePreviewEmailTemplate(key: string) {
  return useMutation({
    mutationFn: async (content: TemplateContent): Promise<PreviewResult> => {
      const res = await fetch(`/api/admin/email/templates/${key}/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: "Render failed" }));
        throw new Error(body.error ?? "Render failed");
      }
      return res.json();
    },
  });
}
